import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Chat } from '../../models/chat.model';
import { Message } from '../../models/message.model';
import { Sender } from '../../models/sender.model';
import { environment } from '../../../environments/environment';
import { Numero } from '../../models/numero.model';
import { LoginResponse, LoginRequest, RefreshTokenResponse } from '../../models/login-request.interface';
import {
  ForceExitMenuRequest,
  ForceExitMenuResponse,
  SendPlainMessageRequest,
  SendSpecialMessageRequest,
  MessageResponse,
  SendInternalObservationRequest,
} from '../../models/message.interface';
import { Page } from '../../models/page.model';
import { Area } from '../../models/area.model';
import {
  DerivacionAbiertaPage,
  DerivacionRequest,
  DerivacionResponse,
} from '../../models/derivacion.model';
import {
  MessagePageResponse,
  SidebarChatItemDto,
  SidebarChatPageDto,
  SidebarChatPageResponse,
} from '../../models/messaging-pages.model';
import { Conversation } from '../../models/conversation.model';
import { ExternalChatViewers } from '../../models/sse-chatlink-events.model';
import {
  ClienteGestion,
  ClienteReferencia,
  CrearPotencialClienteRequest,
  GuardarAsociacionesClienteRequest,
} from '../../models/cliente-referencia.model';
import { CreateAndCloseTicketRequest, TicketCloseResponse, TicketCreationStatus, TicketFormData, TicketOption, TicketClientOption, TicketWebUser, TicketTimeProposal } from '../../models/ticket.model';
import { TicketReconciliationHistoryPage, TicketReconciliationItem, TicketReconciliationResult } from '../../models/ticket-reconciliation.model';
import { TicketListItem } from '../../models/ticket-list.model';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private apiUrl: string = environment.apiUrl;

  constructor(private http: HttpClient) { }

  validarContactoManualmente(waId: string): Observable<{
    message: string;
    waId: string;
    whatsappReachabilityStatus: 'UNKNOWN' | 'VALID' | 'REJECTED';
    lastWhatsappValidationAt: string | null;
  }> {
    return this.http.post<{
      message: string;
      waId: string;
      whatsappReachabilityStatus: 'UNKNOWN' | 'VALID' | 'REJECTED';
      lastWhatsappValidationAt: string | null;
    }>(`${this.apiUrl}/sidebar/contacts/${encodeURIComponent(waId)}/validate-manually`, {});
  }

  setExternalChatPresence(contactKey: string, connectionId: string): Observable<ExternalChatViewers> {
    return this.http.put<ExternalChatViewers>(`${this.apiUrl}/chat/presence/current`, { contactKey, connectionId });
  }

  clearExternalChatPresence(connectionId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/chat/presence/current`, { params: { connectionId } });
  }

  getExternalChatViewers(senderId: number): Observable<ExternalChatViewers> {
    return this.http.get<ExternalChatViewers>(`${this.apiUrl}/chat/presence/${senderId}`);
  }

  resolveMediaUrl(url?: string | null): string {
    if (!url) {
      return '';
    }
    if (/^https?:\/\//i.test(url)) {
      return url;
    }
    const normalized = url.startsWith('/') ? url : `/${url}`;
    return `${this.apiUrl}${normalized}`;
  }


  getDatos(): Observable<any> {
    return this.http.get(`${this.apiUrl}/mensajes`);
  }

  sendData(data: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/mensajes`, data);
  }

  validarCredenciales(data: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/login/iniciarSesion`, data);
  }

  refreshToken(): Observable<RefreshTokenResponse> {
    return this.http.post<RefreshTokenResponse>(
      `${this.apiUrl}/login/refresh-token`,
      null,
    );
  }

  getChats(
    filtro: string,
    page: number = 0,
    size: number = 15,
  ): Observable<SidebarChatPageResponse> {
    return this.http.get<SidebarChatPageDto>(`${this.apiUrl}/sidebar/findChats`, {
      params: {
        filtro: filtro,
        page: page,
        size: size,
      },
      headers: { 'Content-Type': 'application/json' },
    }).pipe(
      map((response) => ({
        ...response,
        content: (response?.content || []).map((chat) =>
          this.normalizeSidebarChat(chat),
        ),
      })),
    );
  }

  getChatByConversationId(conversationId: number): Observable<Chat> {
    return this.http
      .get<SidebarChatItemDto>(`${this.apiUrl}/chat/conversations/${conversationId}`)
      .pipe(map(chat => this.normalizeSidebarChat(chat)));
  }

  getOpenUnreadChatCount(): Observable<{ unreadCount: number }> {
    return this.http.get<{ unreadCount: number }>(`${this.apiUrl}/sidebar/open-unread-count`);
  }

  getMessages(wa_Id: string, size: number = 20): Observable<MessagePageResponse> {
    return this.http.get<MessagePageResponse>(
      `${this.apiUrl}/sidebar/messages/${wa_Id}`,
      {
        params: { size },
        headers: { 'Content-Type': 'application/json' },
      },
    ).pipe(map((response) => this.normalizeMessagePage(response)));
  }

  marcarMensajeLeidoByWaId(waId: string): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/sidebar/read/${waId}`,
      {},
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  marcarMensajeLeido(chat: Chat): Observable<any> {
    return this.marcarMensajeLeidoByWaId(chat.sender.waId);
  }

  marcarChatNoLeidoByWaId(waId: string): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/sidebar/unread/${waId}`,
      {},
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  sendPlaneMessage(message: SendPlainMessageRequest): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(
      `${this.apiUrl}/chat/sendMessage/send-plane-message`,
      message,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }
  sendSpecialMessage(message: SendSpecialMessageRequest): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(
      `${this.apiUrl}/chat/sendMessage/send-special-message`,
      message,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  sendInternalObservation(message: SendInternalObservationRequest): Observable<Message> {
    return this.http.post<Message>(
      `${this.apiUrl}/chat/sendMessage/internal-observation`,
      message,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  forceExitMenu(request: ForceExitMenuRequest): Observable<ForceExitMenuResponse> {
    return this.http.post<ForceExitMenuResponse>(
      `${this.apiUrl}/chat/action/force-exit-menu`,
      request,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  crearEncabezadoAutorizacion(): HttpHeaders {
    return new HttpHeaders();
  }

  obtenerChatsMasAntiguos(listWaIds: string[]): Observable<Chat[]> {
    return this.http.get<Chat[]>(
      `${this.apiUrl}/sidebar/obtenerChatsMasAntiguos`,
      {
        params: {
          listWaIds: listWaIds,
        },
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  buscarContactosPage(search: string = '', page: number = 0, size: number = 20, onlyValid: boolean = false): Observable<Page<Sender>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('onlyValid', String(onlyValid));
      
    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<Page<Sender>>(
      `${this.apiUrl}/sidebar/chats`,
      {
        params,
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  buscarClientesGestion(query: string, page: number = 0, size: number = 20): Observable<Page<ClienteGestion>> {
    return this.http.get<Page<ClienteGestion>>(`${this.apiUrl}/clientes/gestion`, { params: { query, page, size } });
  }

  buscarClientesGestionPorIds(ids: number[]): Observable<ClienteGestion[]> {
    const params = new HttpParams().set('ids', ids.join(','));
    return this.http.get<ClienteGestion[]>(`${this.apiUrl}/clientes/gestion/by-ids`, { params });
  }

  buscarPotencialesClientes(query: string, page: number = 0, size: number = 20): Observable<Page<ClienteReferencia>> {
    return this.http.get<Page<ClienteReferencia>>(`${this.apiUrl}/clientes/potenciales`, { params: { query, page, size } });
  }

  crearPotencialCliente(request: CrearPotencialClienteRequest): Observable<ClienteReferencia> {
    return this.http.post<ClienteReferencia>(`${this.apiUrl}/clientes/potenciales`, request);
  }

  editarPotencialCliente(id: number, request: CrearPotencialClienteRequest): Observable<ClienteReferencia> {
    return this.http.put<ClienteReferencia>(`${this.apiUrl}/clientes/potenciales/${id}`, request);
  }

  eliminarPotencialCliente(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/clientes/potenciales/${id}`);
  }

  obtenerClientesContacto(senderId: number): Observable<ClienteReferencia[]> {
    return this.http.get<ClienteReferencia[]>(`${this.apiUrl}/clientes/contactos/${senderId}`);
  }

  guardarClientesContacto(senderId: number, request: GuardarAsociacionesClienteRequest): Observable<ClienteReferencia[]> {
    return this.http.put<ClienteReferencia[]>(`${this.apiUrl}/clientes/contactos/${senderId}`, request);
  }

  buscarUsuariosWebGestion(clienteId: number, query = ''): Observable<TicketWebUser[]> {
    return this.http.get<TicketWebUser[]>(`${this.apiUrl}/clientes/gestion/${clienteId}/usuarios-web`, {
      params: { query },
    });
  }

  guardarContacto(contacto: Sender): Observable<Sender> {
    return this.http.post<Sender>(
      `${this.apiUrl}/sidebar/contacts/guardarContacto`,
      contacto,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  obtenerNumerosPropios(): Observable<Numero[]> {
    return this.http.get<Numero[]>(
      `${this.apiUrl}/sidebar/findNumeros`,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  cambiarNumeroActual(numero: Numero): Observable<Numero> {
    return this.http.post<Numero>(
      `${this.apiUrl}/configuracion/cambiarNumeroActual`,
      numero,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  cerrarChat(
    conversacionId: string,
    waId: string,
    notificarCliente: boolean = true,
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/chat/closeChat`,
      {},
      {
        params: { conversacionId, waId, notificarCliente },
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  getTicketFormData(conversationId: string, waId: string): Observable<TicketFormData> {
    return this.http.get<TicketFormData>(`${this.apiUrl}/chat/tickets/form-data`, {
      params: { conversationId, waId },
    });
  }

  getTicketTimeProposal(conversationId: string, waId: string): Observable<TicketTimeProposal> {
    return this.http.get<TicketTimeProposal>(`${this.apiUrl}/chat/tickets/time-proposal`, {
      params: { conversationId, waId },
    });
  }

  getTicketProjects(clienteId: number, conversationId: string, waId: string): Observable<TicketOption[]> {
    return this.http.get<TicketOption[]>(`${this.apiUrl}/chat/tickets/clients/${clienteId}/projects`, {
      params: { conversationId, waId },
    });
  }

  getGestionClientProjects(clienteId: number): Observable<TicketOption[]> {
    return this.http.get<TicketOption[]>(`${this.apiUrl}/chat/tickets/gestion-clients/${clienteId}/projects`);
  }

  getTicketWebUsers(clienteId: number, conversationId: string, waId: string, query = ''): Observable<TicketWebUser[]> {
    return this.http.get<TicketWebUser[]>(`${this.apiUrl}/chat/tickets/clients/${clienteId}/web-users`, {
      params: { conversationId, waId, query },
    });
  }

  getTicketWebUser(clienteId: number, usuarioId: number, conversationId: string, waId: string): Observable<TicketWebUser> {
    return this.http.get<TicketWebUser>(`${this.apiUrl}/chat/tickets/clients/${clienteId}/web-users/${usuarioId}`, {
      params: { conversationId, waId },
    });
  }

  linkTicketWebUser(clienteId: number, conversationId: string, waId: string, usuarioWebId: number | null): Observable<TicketClientOption> {
    return this.http.put<TicketClientOption>(`${this.apiUrl}/chat/tickets/clients/${clienteId}/web-user-linkage`,
      { usuarioWebId }, { params: { conversationId, waId } });
  }

  searchTicketGestionClients(conversationId: string, waId: string, query: string): Observable<TicketClientOption[]> {
    return this.http.get<TicketClientOption[]>(`${this.apiUrl}/chat/tickets/${conversationId}/gestion-clients`, {
      params: { waId, query },
    });
  }

  linkTicketClient(conversationId: string, waId: string, request: {
    gestionClienteId?: number; nombre?: string;
  }): Observable<TicketClientOption> {
    return this.http.post<TicketClientOption>(`${this.apiUrl}/chat/tickets/${conversationId}/client-linkage`, request, { params: { waId } });
  }

  unlinkTicketClientLinkage(conversationId: string, waId: string, clienteReferenciaId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/chat/tickets/${conversationId}/client-linkage`, {
      params: { waId, clienteReferenciaId: String(clienteReferenciaId) },
    });
  }

  getTicketSubjectSuggestions(query: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/chat/tickets/subjects`, { params: { query } });
  }

  getCreatedTickets(filters: {
    clienteId?: number | null;
    desde?: string;
    hasta?: string;
    page?: number;
    size?: number;
    sort?: string;
    direction?: 'asc' | 'desc';
  }): Observable<Page<TicketListItem>> {
    let params = new HttpParams()
      .set('page', String(filters.page ?? 0))
      .set('size', String(filters.size ?? 20))
      .set('sort', filters.sort ?? 'fechaCreacion')
      .set('direction', filters.direction ?? 'desc');

    if (filters.clienteId != null) params = params.set('clienteId', String(filters.clienteId));
    if (filters.desde) params = params.set('desde', filters.desde);
    if (filters.hasta) params = params.set('hasta', filters.hasta);

    return this.http.get<Page<TicketListItem>>(`${this.apiUrl}/chat/tickets`, { params });
  }

  createTicketAndClose(request: CreateAndCloseTicketRequest, files: File[] = []): Observable<TicketCloseResponse> {
    if (!files.length) {
      return this.http.post<TicketCloseResponse>(`${this.apiUrl}/chat/tickets/close-with-ticket`, request);
    }
    const formData = new FormData();
    formData.append('ticket', new Blob([JSON.stringify(request)], { type: 'application/json' }));
    files.forEach((file) => formData.append('files', file, file.name));
    return this.http.post<TicketCloseResponse>(`${this.apiUrl}/chat/tickets/close-with-ticket`, formData);
  }

  retryTicketConversationClose(conversationId: string, waId: string): Observable<TicketCloseResponse> {
    return this.http.post<TicketCloseResponse>(`${this.apiUrl}/chat/tickets/retry-close`, { conversationId, waId });
  }

  getTicketCreationStatus(conversationId: string): Observable<TicketCreationStatus> {
    return this.http.get<TicketCreationStatus>(`${this.apiUrl}/chat/tickets/${encodeURIComponent(conversationId)}/status`);
  }

  getTicketReconciliationRequired(): Observable<TicketReconciliationItem[]> {
    return this.http.get<TicketReconciliationItem[]>(`${this.apiUrl}/chat/tickets/reconciliation-required`);
  }

  getTicketReconciliationHistory(page = 0, size = 20): Observable<TicketReconciliationHistoryPage> {
    return this.http.get<TicketReconciliationHistoryPage>(`${this.apiUrl}/chat/tickets/reconciliation-history`, {
      params: { page, size },
    });
  }

  reconcileTicket(conversationId: string, ticketCreated: boolean, ticketNumber: number | null): Observable<TicketReconciliationResult> {
    return this.http.post<TicketReconciliationResult>(
      `${this.apiUrl}/chat/tickets/${encodeURIComponent(conversationId)}/reconcile`,
      { ticketCreated, ticketNumber },
    );
  }

  recoverTicketWrite(conversationId: string, expectedVersion: number): Observable<TicketReconciliationResult> {
    return this.http.post<TicketReconciliationResult>(
      `${this.apiUrl}/chat/tickets/${encodeURIComponent(conversationId)}/recover`,
      { expectedVersion },
    );
  }

  registerTicketReference(conversationId: string, ticketNumber: number): Observable<TicketReconciliationResult> {
    return this.http.post<TicketReconciliationResult>(
      `${this.apiUrl}/chat/tickets/${encodeURIComponent(conversationId)}/register-reference`,
      { ticketNumber },
    );
  }

  linkTemporaryClient(senderId: number, temporalReferenceId: number, request: { gestionClienteId: number }): Observable<any> {
    return this.http.post(`${this.apiUrl}/clientes/contactos/${senderId}/temporales/${temporalReferenceId}/vincular-gestion`, request);
  }

  updateTemporaryClientLinkage(senderId: number, temporalReferenceId: number, request: { gestionClienteId: number }): Observable<any> {
    return this.http.put(`${this.apiUrl}/clientes/contactos/${senderId}/temporales/${temporalReferenceId}/vincular-gestion`, request);
  }

  getTemporaryTickets(page = 0, size = 20): Observable<import('../../models/temporary-ticket.model').PendingTemporaryTicketPage> {
    return this.http.get<import('../../models/temporary-ticket.model').PendingTemporaryTicketPage>(`${this.apiUrl}/chat/tickets/temporary-tickets`, {
      params: { page, size },
    });
  }

  getTemporaryClients(): Observable<import('../../models/temporary-ticket.model').TemporaryClientAdmin[]> {
    return this.http.get<import('../../models/temporary-ticket.model').TemporaryClientAdmin[]>(`${this.apiUrl}/chat/tickets/temporary-clients`);
  }

  linkTemporaryAdminClient(temporaryId: number, gestionClienteId: number): Observable<import('../../models/temporary-ticket.model').TemporaryClientAdmin> {
    return this.http.post<import('../../models/temporary-ticket.model').TemporaryClientAdmin>(`${this.apiUrl}/chat/tickets/temporary-clients/${temporaryId}/linkage`, { gestionClienteId });
  }

  updateTemporaryAdminClient(temporaryId: number, gestionClienteId: number): Observable<import('../../models/temporary-ticket.model').TemporaryClientAdmin> {
    return this.http.put<import('../../models/temporary-ticket.model').TemporaryClientAdmin>(`${this.apiUrl}/chat/tickets/temporary-clients/${temporaryId}/linkage`, { gestionClienteId });
  }

  getTemporaryProjects(): Observable<import('../../models/temporary-ticket.model').TemporaryProjectAdmin[]> {
    return this.http.get<import('../../models/temporary-ticket.model').TemporaryProjectAdmin[]>(`${this.apiUrl}/chat/tickets/temporary-projects`);
  }

  linkTemporaryAdminProject(projectId: number, request: { gestionClienteId: number; proyectoId: number }): Observable<import('../../models/temporary-ticket.model').TemporaryProjectAdmin> {
    return this.http.post<import('../../models/temporary-ticket.model').TemporaryProjectAdmin>(`${this.apiUrl}/chat/tickets/temporary-projects/${projectId}/linkage`, request);
  }

  linkTemporaryAdminTicket(conversationId: string, request: {
    temporaryClientReferenceId: number;
    gestionClienteId: number;
    proyectoId: number;
  }): Observable<import('../../models/temporary-ticket.model').TemporaryLinkageResponse> {
    return this.http.post<import('../../models/temporary-ticket.model').TemporaryLinkageResponse>(
      `${this.apiUrl}/chat/tickets/temporary-tickets/${encodeURIComponent(conversationId)}/linkage`, request,
    );
  }

  enviarEncuesta(conversacionId: string, waId: string): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/chat/sendSurvey`,
      {},
      { params: { conversacionId, waId }, headers: { 'Content-Type': 'application/json' } }
    );
  }

  obtenerMensajesMasAntiguos(
    waId: string,
    conversationId: number,
    beforeTimestamp: string | null,
    beforeMessageId: number | string | null,
    size: number = 20,
  ): Observable<MessagePageResponse> {
    let params = new HttpParams()
      .set('waId', waId)
      .set('conversationId', conversationId.toString())
      .set('size', size.toString());

    if (beforeTimestamp) {
      params = params.set('beforeTimestamp', beforeTimestamp);
    }

    if (beforeMessageId !== null && beforeMessageId !== undefined) {
      params = params.set('beforeMessageId', String(beforeMessageId));
    }

    return this.http.get<MessagePageResponse>(`${this.apiUrl}/chat/messages/getOlderMessages`, {
      params,
      headers: { 'Content-Type': 'application/json' }
    }).pipe(map((response) => this.normalizeMessagePage(response)));
  }

  getDerivacionAreas(): Observable<Area[]> {
    return this.http.get<Area[]>(`${this.apiUrl}/derivacion/areas`);
  }

  getDerivacionesAbiertasAsignadas(page = 0, size = 50): Observable<DerivacionAbiertaPage> {
    return this.http.get<DerivacionAbiertaPage>(`${this.apiUrl}/derivacion/abiertas`, {
      params: { page, size },
    });
  }

  derivarConversacionByWaId(request: DerivacionRequest): Observable<DerivacionResponse> {
    return this.http.post<DerivacionResponse>(`${this.apiUrl}/derivacion`, request, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  derivarConversacionById(conversacionId: number, request: DerivacionRequest): Observable<DerivacionResponse> {
    return this.http.put<DerivacionResponse>(`${this.apiUrl}/derivacion/${conversacionId}`, request, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  private normalizeSidebarChat(chat: SidebarChatItemDto): Chat {
    const conversation = this.normalizeSidebarConversation(chat.conversacion);
    const lastMessage = chat.lastMessage
      ? this.normalizeMessage(chat.lastMessage, conversation, chat.sender)
      : undefined;

    return {
      ...chat,
      sender: {
        ...chat.sender,
        mensajesNuevos: chat.mensajesNuevos ?? chat.sender?.mensajesNuevos ?? false,
      },
      listMessages: chat.listMessages || [],
      unreadCount: chat.unreadCount || 0,
      conversacion: conversation,
      lastMessage,
      estadoConversacion:
        chat.estadoConversacion || this.getEstadoConversacion(conversation),
    };
  }

  private normalizeSidebarConversation(
    conversation: SidebarChatItemDto['conversacion'],
  ): Conversation {
    if ('isClosed' in conversation) {
      return conversation;
    }

    return {
      id: conversation.id,
      lastTimestamp: '',
      waId: conversation.waId,
      isClosed: conversation.closed,
      isPendienteEnvioMensaje: false,
      isPendienteEnvioEncuesta: conversation.pendienteEnvioEncuesta,
      menuActivo: conversation.menuActivo,
      area: {
        id: conversation.areaId,
        descripcion: conversation.areaDescripcion,
      },
      usuarioAsignado: conversation.usuarioAsignado,
      nombreCompletoAsignado: conversation.nombreCompletoAsignado,
      rolAsignado: conversation.rolAsignado,
    };
  }

  private normalizeMessagePage(response: MessagePageResponse): MessagePageResponse {
    return {
      ...response,
      content: (response?.content || []).map((message) =>
        this.normalizeMessage(message),
      ),
      hasMore: !!response?.hasMore,
      nextBeforeMessageId: response?.nextBeforeMessageId ?? null,
      nextBeforeTimestamp: response?.nextBeforeTimestamp ?? null,
      activeConversationId: Number(response?.activeConversationId ?? 0),
      conversationId: Number(response?.conversationId ?? 0),
    };
  }

  private normalizeMessage(
    message: Message,
    fallbackConversation?: Conversation,
    fallbackSender?: Sender,
  ): Message {
    const internalId = message.id ?? (message as any).id;
    const messageId = String(message.messageId || internalId || '');
    const sender = message.sender || fallbackSender || {
      id: 0,
      waId: fallbackConversation?.waId || '',
      name: '',
      number: fallbackConversation?.waId || '',
      correoElectronico: '',
      notas: '',
      mensajesNuevos: false,
    };
    const conversacion = message.conversacion || fallbackConversation || {
      id: 0,
      lastTimestamp: message.timestamp || '',
      waId: sender.waId || sender.number || '',
      isClosed: false,
      isPendienteEnvioMensaje: false,
      isPendienteEnvioEncuesta: false,
      menuActivo: false,
      area: {
        id: 0,
        descripcion: '',
      },
    };

    return {
      ...message,
      id: internalId,
      messageId,
      sender,
      media: message.media || {
        id: 0,
        mimeType: '',
        mediaUrl: '',
        thumbnailUrl: '',
      },
      conversacion,
    };
  }

  private getEstadoConversacion(
    conversation: Conversation,
  ): 'ABIERTA' | 'ESPERANDO_ENCUESTA' | 'CERRADA' {
    if (!conversation.isClosed) {
      return 'ABIERTA';
    }

    return conversation.isPendienteEnvioEncuesta
      ? 'ESPERANDO_ENCUESTA'
      : 'CERRADA';
  }

}

